import { c as _c } from "react/compiler-runtime";
// import {theme} from 'components/theme'
import { ScaleLinear } from 'd3-scale';
import { CSSProperties, ReactNode } from 'react';
import { match } from 'ts-pattern';
import { Fmt } from 'shared/forecast/Fmt';
import { fmtWholePct } from 'shared/format';
import { StyleProps } from 'shared/ui';
import { G } from '../svg/G';
import { Rnge } from '../../range';
import { useSafeMantineTheme } from '@mantine/core';
import { isString } from 'remeda';
export type XAxisLabelsProps = Pick<XMetricLabelProps, 'scale' | 'format' | 'showTick' | 'styles' | 'classes' | 'style' | 'className'> & {
  placement?: 'center-tick' | 'book-ends';
};
export const XAxisLabelsG = t0 => {
  const $ = _c(30);
  const {
    placement,
    ...props
  } = t0;
  const {
    colors
  } = useSafeMantineTheme();
  const [start, end] = props.scale.domain();
  const t1 = props?.styles?.number;
  let t2;
  if ($[0] !== colors.gray[4] || $[1] !== t1) {
    t2 = {
      fill: colors.gray[4],
      fontWeight: "normal",
      ...t1
    };
    $[0] = colors.gray[4];
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== props.styles || $[4] !== t2) {
    t3 = {
      ...props.styles,
      number: t2
    };
    $[3] = props.styles;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== props || $[7] !== t3) {
    t4 = {
      ...props,
      size: "0.85em",
      styles: t3
    };
    $[6] = props;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const labelProps = t4 satisfies Partial<XMetricLabelProps>;
  const t5 = placement == "book-ends" ? "start" : "middle";
  let t6;
  if ($[9] !== t5) {
    t6 = {
      textAnchor: t5
    };
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== labelProps.styles || $[12] !== t6) {
    t7 = {
      ...labelProps.styles,
      text: t6
    };
    $[11] = labelProps.styles;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== labelProps || $[15] !== start || $[16] !== t7) {
    t8 = <XMetricLabelG value={start} {...labelProps} styles={t7} />;
    $[14] = labelProps;
    $[15] = start;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = placement == "book-ends" ? "end" : "middle";
  let t10;
  if ($[18] !== t9) {
    t10 = {
      textAnchor: t9
    };
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== labelProps.styles || $[21] !== t10) {
    t11 = {
      ...labelProps.styles,
      text: t10
    };
    $[20] = labelProps.styles;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== end || $[24] !== labelProps || $[25] !== t11) {
    t12 = <XMetricLabelG value={end} {...labelProps} styles={t11} />;
    $[23] = end;
    $[24] = labelProps;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== t8) {
    t13 = <>{t8}{t12}</>;
    $[27] = t12;
    $[28] = t8;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
};
export const XLabel = t0 => {
  const $ = _c(23);
  const {
    value,
    scale,
    classes,
    textHeight,
    variant: t1,
    showTicks,
    fmt: t2
  } = t0;
  const variant = t1 === undefined ? "subtle" : t1;
  const fmt = t2 === undefined ? fmtWholePct : t2;
  let t3;
  if ($[0] !== scale || $[1] !== value) {
    t3 = scale(value);
    $[0] = scale;
    $[1] = value;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const medianX = t3;
  const t4 = `${classes?.text ?? ""}`;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      dominantBaseline: "auto",
      textAnchor: "middle"
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = ` ${variant == "subtle" ? "fill-gray-500 text-sm font-semibold" : "fill-gray-600 text-4xl font-bold"} ${classes?.textNum ?? ""}`;
  let t7;
  if ($[4] !== fmt || $[5] !== value) {
    t7 = fmt(value);
    $[4] = fmt;
    $[5] = value;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t6 || $[8] !== t7) {
    t8 = <tspan className={t6}>{t7}</tspan>;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== medianX || $[11] !== t4 || $[12] !== t8 || $[13] !== textHeight) {
    t9 = <g><text className={t4} x={medianX} y={textHeight} style={t5}>{t8}</text></g>;
    $[10] = medianX;
    $[11] = t4;
    $[12] = t8;
    $[13] = textHeight;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== classes?.line || $[16] !== medianX || $[17] !== showTicks || $[18] !== textHeight) {
    t10 = showTicks && <g style={{
      marginTop: 2,
      marginBottom: 2
    }}><line x1={medianX} x2={medianX} y1={textHeight + 6} y2={textHeight + 14} className={`stroke-gray-300 ${classes?.line ?? ""}`} /></g>;
    $[15] = classes?.line;
    $[16] = medianX;
    $[17] = showTicks;
    $[18] = textHeight;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t9) {
    t11 = <>{t9}{t10}</>;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
};
export type XMetricLabelProps = {
  value: number;
  format?: string | ((value: number) => string);
  place?: 'above' | 'below';
  xPlace?: 'inside' | 'middle' | 'overhang';
  label?: string;
  color?: string;
  classes?: {
    text?: string;
    label?: string;
    number?: string;
  };
  styles?: {
    text?: CSSProperties;
    label?: CSSProperties;
    number?: CSSProperties;
    tick?: CSSProperties;
  };
  size?: string;
  showTick?: boolean;
  hideLabel?: boolean;
  tip?: ReactNode;
} & {
  scale: ScaleLinear<number, number>;
} & StyleProps;
export const XMetricLabelG = t0 => {
  const $ = _c(28);
  const {
    value,
    format,
    scale,
    place: t1,
    label: title,
    classes,
    styles,
    color,
    size,
    showTick,
    hideLabel
  } = t0;
  const place = t1 === undefined ? "below" : t1;
  const {
    colors
  } = useSafeMantineTheme();
  const x = scale(value);
  const loc = title == null ? "middle" : value < Rnge.middleOf(scale.domain()) ? "right" : "left";
  const t2 = place == "above" ? "column" : "column-reverse";
  let t3;
  if ($[0] !== t2) {
    t3 = {
      display: "flex",
      flexDirection: t2
    };
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== classes?.label || $[3] !== classes?.number || $[4] !== classes?.text || $[5] !== color || $[6] !== format || $[7] !== hideLabel || $[8] !== loc || $[9] !== size || $[10] !== styles?.label || $[11] !== styles?.number || $[12] !== styles?.text || $[13] !== title || $[14] !== value || $[15] !== x) {
    t4 = !hideLabel && <g><text className={`${classes?.text ?? ""}`} x={x + match(loc).with("right", _temp).with("left", _temp2).otherwise(_temp3)} dy="1em" style={{
        textAnchor: match(loc).with("right", _temp4).with("left", _temp5).otherwise(_temp6),
        fontSize: size,
        ...styles?.text
      }}><tspan className={`fill-gray-700 text-[1.2em] font-extrabold font-numbers ${classes?.number}`} style={{
          fill: color,
          ...styles?.number
        }}>{isString(format) || format == null ? Fmt.fmt(value, {
            fmt: format
          }) : format(value)}</tspan>{title && <tspan dx={4} className={`fill-gray-500 ${classes?.label}`} style={{
          fill: color,
          ...styles?.label
        }}>{`  ${title}`}</tspan>}</text></g>;
    $[2] = classes?.label;
    $[3] = classes?.number;
    $[4] = classes?.text;
    $[5] = color;
    $[6] = format;
    $[7] = hideLabel;
    $[8] = loc;
    $[9] = size;
    $[10] = styles?.label;
    $[11] = styles?.number;
    $[12] = styles?.text;
    $[13] = title;
    $[14] = value;
    $[15] = x;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== color || $[18] !== colors || $[19] !== hideLabel || $[20] !== showTick || $[21] !== styles?.tick || $[22] !== x) {
    t5 = showTick && <g style={{
      marginTop: 2,
      marginBottom: 2
    }}><line x1={x} x2={x} y2={hideLabel ? 10 : 5} style={{
        stroke: color ?? colors.gray[6],
        strokeWidth: "3px",
        strokeLinecap: "round",
        ...styles?.tick
      }} /></g>;
    $[17] = color;
    $[18] = colors;
    $[19] = hideLabel;
    $[20] = showTick;
    $[21] = styles?.tick;
    $[22] = x;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== t3 || $[25] !== t4 || $[26] !== t5) {
    t6 = <G style={t3}>{t4}{t5}</G>;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  return t6;
};
function _temp() {
  return -18;
}
function _temp2() {
  return 18;
}
function _temp3() {
  return 0;
}
function _temp4(_) {
  return "start";
}
function _temp5(__0) {
  return "end";
}
function _temp6(__1) {
  return "middle";
}